import { useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Heart, Copy, Check, RefreshCw, Mic, Image as ImageIcon, Inbox,
} from "lucide-react";

function ContributionCard({ c, onApprove, onDecline, busy }) {
  return (
    <div data-testid={`contribution-${c.id}`} className="bg-white rounded-2xl border border-line p-6 animate-fade-up">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-coral bg-coral/10 px-2.5 py-1 rounded-full">
          <Heart size={12} /> Shared by {c.contributor_name}
        </span>
        {c.source === "voice" && (
          <span className="inline-flex items-center gap-1 text-xs text-teal bg-sagelight px-2 py-1 rounded-full"><Mic size={12} /> Voice</span>
        )}
      </div>
      <h3 className="font-serif text-lg text-charcoal mt-3 leading-snug">{c.question_text}</h3>
      <p className="mt-2 text-charcoal/80 leading-relaxed whitespace-pre-wrap">{c.original_transcript}</p>

      {c.photo_file_id && (
        <img src={fileUrl(c.photo_file_id)} alt={c.photo_caption || "Shared photo"}
          className="mt-4 rounded-xl border border-line max-h-64 object-cover" />
      )}
      {c.audio_id && (
        <audio data-testid={`contribution-audio-${c.id}`} controls src={fileUrl(c.audio_id)} className="mt-4 w-full" />
      )}

      {c.status === "pending" ? (
        <div className="mt-5 flex gap-3">
          <button data-testid={`approve-contribution-${c.id}`} onClick={() => onApprove(c.id)} disabled={busy}
            className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Add to my story
          </button>
          <button data-testid={`decline-contribution-${c.id}`} onClick={() => onDecline(c.id)} disabled={busy}
            className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-subtle px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
            Not now
          </button>
        </div>
      ) : (
        <p className={`mt-4 text-sm font-semibold ${c.status === "approved" ? "text-teal" : "text-subtle"}`}>
          {c.status === "approved" ? "Added to your story" : "Declined"}
        </p>
      )}
    </div>
  );
}

export default function Family() {
  const [link, setLink] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const shareUrl = link ? `${window.location.origin}${link.path}` : "";

  const load = async () => {
    const [l, c] = await Promise.all([api.get("/family/link"), api.get("/family/contributions")]);
    setLink(l.data); setItems(c.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied — share it with your family");
    } catch { toast.error("Couldn't copy. Please copy the link manually."); }
  };

  const regenerate = async () => {
    if (!window.confirm("Create a new link? The old link will stop working.")) return;
    const { data } = await api.post("/family/link/regenerate");
    setLink((prev) => ({ ...prev, ...data }));
    toast.success("A fresh link is ready");
  };

  const approve = async (id) => {
    setBusyId(id);
    try { await api.post(`/family/contributions/${id}/approve`); toast.success("Added to your story"); await load(); }
    catch { toast.error("Couldn't add that right now. Please try again."); }
    finally { setBusyId(null); }
  };
  const decline = async (id) => {
    setBusyId(id);
    try { await api.post(`/family/contributions/${id}/decline`); await load(); }
    catch { toast.error("Couldn't update that. Please try again."); }
    finally { setBusyId(null); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;

  const pending = items.filter((c) => c.status === "pending");
  const reviewed = items.filter((c) => c.status !== "pending");

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="family-page">
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-2xl bg-coral/10 text-coral flex items-center justify-center shrink-0"><Heart size={24} /></span>
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Family &amp; friends</h1>
          <p className="text-subtle mt-1">Invite loved ones to share their own memories of you. You review each one before it becomes part of your story.</p>
        </div>
      </div>

      {/* Share link */}
      <div className="bg-teal rounded-3xl p-7 sm:p-8 text-cream shadow-sm">
        <p className="text-xs uppercase tracking-widest text-cream/60 font-semibold">Your contribution link</p>
        <p className="font-serif text-xl sm:text-2xl leading-snug mt-2">Anyone with this link can add a memory — no account needed.</p>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <input data-testid="family-share-url" readOnly value={shareUrl}
            className="flex-1 bg-cream/10 border border-cream/25 rounded-full px-5 h-12 text-cream text-sm outline-none truncate" />
          <button data-testid="family-copy-btn" onClick={copy}
            className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-6 h-12 rounded-full font-semibold transition-colors">
            {copied ? <Check size={18} /> : <Copy size={18} />} {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        <button data-testid="family-regenerate-btn" onClick={regenerate}
          className="mt-4 inline-flex items-center gap-2 text-cream/80 hover:text-cream text-sm transition-colors">
          <RefreshCw size={14} /> Create a new link
        </button>
        <p data-testid="family-trust-note" className="mt-3 text-sm text-cream/70 leading-relaxed">
          Only share this link with people you trust. You can create a new link at any time to disable the old one.
        </p>
      </div>

      {/* Pending */}
      <div>
        <h2 className="font-serif text-2xl text-charcoal">To review {pending.length > 0 && <span className="text-coral">({pending.length})</span>}</h2>
        {pending.length === 0 ? (
          <div className="mt-4 text-center py-12 bg-white rounded-2xl border border-line">
            <Inbox className="mx-auto text-sage" size={32} />
            <p className="mt-3 text-subtle">No new contributions yet. Share your link and they'll appear here for you to review.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {pending.map((c) => (
              <ContributionCard key={c.id} c={c} onApprove={approve} onDecline={decline} busy={busyId === c.id} />
            ))}
          </div>
        )}
      </div>

      {/* Reviewed */}
      {reviewed.length > 0 && (
        <div>
          <h2 className="font-serif text-2xl text-charcoal">Already reviewed</h2>
          <div className="mt-4 space-y-4">
            {reviewed.map((c) => (
              <ContributionCard key={c.id} c={c} onApprove={approve} onDecline={decline} busy={busyId === c.id} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
